import React from 'react';
import { ScrollReveal } from './animations/ScrollReveal';
import { ImageFadeIn } from './animations/ImageFadeIn';
interface Hero {
  title: string;
  subtitle: string;
  backgroundImage: string;
}
interface Stat {
  value: string;
  label: string;
  icon: ReactNode;
}
interface Card {
  title: string;
  content: string;
  image: string;
}
interface SubpageLayoutProps {
  hero: Hero;
  videoSection?: ReactNode;
  stats: Stat[];
  cards: Card[];
  ctaTitle: string;
  ctaDescription: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  children?: ReactNode;
}
const SubpageLayout: React.FC<SubpageLayoutProps> = ({
  hero,
  videoSection,
  stats,
  cards,
  ctaTitle,
  ctaDescription,
  ctaButtonText,
  ctaButtonLink,
  children
}) => {
  return <div className="w-full min-h-screen bg-white" data-id="element-199">
      {/* Hero Section */}
      <div className="relative min-h-[60vh] flex items-center" data-id="element-200">
        <div className="absolute inset-0 z-0" data-id="element-201">
          <ImageFadeIn src={hero.backgroundImage} alt={hero.title} className="w-full h-full object-cover" data-id="element-202" />
          <div className="absolute inset-0 bg-black/50" data-id="element-203"></div>
        </div>
        <div className="relative z-10 w-full pt-36 sm:pt-52 md:pt-56 pb-16 sm:pb-20" data-id="element-204">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-205">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-4 sm:mb-6" data-id="element-206">
              {hero.title}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-207">
              {hero.subtitle}
            </p>
          </div>
        </div>
      </div>
      {/* Video Section (if provided) */}
      {videoSection}
      {/* Stats Section */}
      <ScrollReveal animation="fade" data-id="element-208">
        <div className="pt-8 sm:pt-12 pb-16 sm:pb-24 bg-gray-50" data-id="element-209">
          <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-210">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-211">
              {stats.map((stat, index) => <ScrollReveal key={index} animation="slide-up" delay={index * 0.1} data-id="element-212">
                  <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 h-full" data-id="element-213">
                    <div className="flex items-center mb-4" data-id="element-214">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4" data-id="element-215">
                        {stat.icon}
                      </div>
                      <div className="text-3xl font-bold text-gray-900" data-id="element-216">
                        {stat.value}
                      </div>
                    </div>
                    <p className="text-gray-600" data-id="element-217">{stat.label}</p>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Cards Section */}
      <ScrollReveal animation="fade" data-id="element-218"></ScrollReveal>
      {/* Custom Content */}
      {children}
      {/* CTA Section */}
      <ScrollReveal animation="fade" data-id="element-219">
        <div className="py-16 sm:py-24 bg-israel-blue text-white" data-id="element-220">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-221">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight" data-id="element-222">
              {ctaTitle}
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto" data-id="element-223">
              {ctaDescription}
            </p>
            <div className="flex flex-wrap justify-center gap-6" data-id="element-224">
              <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-white text-israel-blue font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg shadow-lg" data-id="element-225">
                Donate Now
              </a>
              <a href={ctaButtonLink} className="inline-block px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors text-lg" data-id="element-226">
                {ctaButtonText}
              </a>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>;
};
export default SubpageLayout;